import {
  ReviewPoolItem,
  ReviewSourceTag,
  ReviewDiagnosisType,
  ReviewOperation,
  ReviewContactResult,
} from '../types/reviewPool';

export interface OperationDescriptor {
  key: ReviewOperation;
  recommended: boolean;
  /** 覆盖按钮文案 key（如未联系→创建新站点（未建联））；缺省用 operation.<key> */
  labelKey?: string;
  /** 推荐项副标题 key（组件用 pickup.addressReviewPool.opSubtitle.<key>） */
  subtitleKey?: string;
  /** 按钮前缀文案 key（如「确认非同一站点时：」；组件用 pickup.addressReviewPool.opPrefix.<key>） */
  prefixKey?: string;
  /** 禁用态（如标准化失败时「创建新站点」需先改址通过标准化后才可用） */
  disabled?: boolean;
}

export interface OperationPlan {
  /** 诊断提示文案 key（组件用 pickup.addressReviewPool.hint.<key> 取） */
  diagnosisHintKey: 'new' | 'standardizeFailed' | 'similar';
  /** 未确认联系结果时操作区置灰（PRD：请先确认联系结果） */
  requireContactFirst: boolean;
  /**
   * 2.1 需求点1：标准化失败 + 首次未联系到 ⇒ 还需确认**二次**联系结果，操作区继续置灰。
   * 二次联系结果只在首次未联系到时出现（已联系到就该去改地址，再问二次没有意义），
   * 与既有「一次/二次建联」语义一致 —— link5 的业务前提就是两次电联都没联系到。
   */
  requireSecondContactFirst?: boolean;
  /**
   * 2.1 需求点1：当前联系节点选了「未联系到」但凭证截图还没上传 ⇒ 本节点未完成，操作区置灰。
   * 与 requireSecondContactFirst 一起实现「完成当前节点才能进入下一节点」的顺序门禁：
   * 第1步选未联系到 → 传截图 → 第2步才出现 → 选未联系到 → 传二次截图 → 第3步才可操作。
   * 少了这个标志，运营删掉第1步截图后第2步会消失、第3步却还显示终态操作（只是置灰），节点顺序被打断。
   */
  requireProofFirst?: boolean;
  /** 「选择操作」场景引导文案 key（组件用 pickup.addressReviewPool.opGuide.<key>） */
  guidanceKey?: string;
  operations: OperationDescriptor[];
}

const isHistory = (it: ReviewPoolItem) => it.sourceTag === ReviewSourceTag.History;

/**
 * 按 PRD「选择操作」矩阵（来源 × 诊断 × 联系结果）计算可执行操作与推荐项。
 * 待评审项（如确认无法建联是否拦截）不在此处写死，仅按 PRD 已定义分支。
 *
 * hasProof：是否已上传联络记录截图。PRD 规定「未联系到客户」需先上传联络凭证方可建站，
 * 故未联系 + 系统/BD 的「创建新站点（未建联）」在无截图时置灰不可操作；缺省 true（不拦截其余入口）。
 */
export function getOperationPlan(
  item: ReviewPoolItem,
  contact?: ReviewContactResult,
  hasProof = true,
  secondContact?: ReviewContactResult,
  hasSecondProof = true,
): OperationPlan {
  const stdFail = item.diagnosisType === ReviewDiagnosisType.StandardizeFailed;
  /**
   * 2.2 需求点1：已上报地址异常的条目走**同一条免上门流程**（两步电联 → 终态确认无法建联）。
   *
   * <p>只在没有任何一次"已联系到"时才并入 stdFail 分支：地址异常上报的前提是地址本身标准化成功、
   * 只是上不了门，所以一旦联系上了客户，该走的是常规建站/合并流程，而不是 stdFail 的「先改地址」——
   * 那条路是给"地址本身不可用"准备的。此时详情面板会提示运营先撤销上报（显然是误报）。
   */
  const abnormalNoOnsite =
    !!item.abnormalReport && contact !== 'contacted' && secondContact !== 'contacted';
  const similar = item.diagnosisType === ReviewDiagnosisType.Similar;
  const hintKey: OperationPlan['diagnosisHintKey'] = stdFail ? 'standardizeFailed' : similar ? 'similar' : 'new';

  // 未确认联系结果 → 操作区置灰
  if (!contact) {
    return { diagnosisHintKey: hintKey, requireContactFirst: true, operations: [] };
  }

  const ops: OperationDescriptor[] = [];
  let guidanceKey: string;
  // ============ 2.1 需求点1：标准化失败 → 免司机上门的两步联系流程 ============
  // 优先于联系结果分支：地址本身不可用，走的是独立流程（司机上门整段消失）。
  //
  // **不再排除历史存量**：产品口径「只要是标准化失败，统一走这个流程」，不分 source_tag。
  // 2.0 曾写 `stdFail && !isHistory`，会让 history+stdFail 掉进历史存量分支拿到
  // 「确认/创建当日预约单」，与 2.1 流程不符。
  //
  // **推翻 2.0「不给创建新站点」的结论**（产品定案：2.1 就是为解决 2.0 的问题）。
  // 2.0 的理由是「补录地址不落库 ⇒ 该按钮永远解不了锁，且落库会毁掉 AB 的命中键」；
  // 2.1 机制不同：不补录、不调地址库，直接用条目原始地址 + 条目自带的 core_hash 建站
  // （skipAddressStandardize）。已核实 AB 命中先于站点命中返回，故存在同 hash 的站点
  // **不会**削弱后续 A→B 的 AB 映射 —— 那个担心不成立。详见 plan §2.6 影响范围评估。
  if (stdFail || abnormalNoOnsite) {
    // 顺序门禁：逐节点放行，任一节点未完成就不给下一节点/操作区。
    const gate = (flag: 'requireProofFirst' | 'requireSecondContactFirst'): OperationPlan => ({
      diagnosisHintKey: hintKey,
      requireContactFirst: false,
      requireSecondContactFirst: flag === 'requireSecondContactFirst',
      requireProofFirst: flag === 'requireProofFirst',
      operations: [],
    });
    if (contact === 'not_contacted') {
      // 第1节点：选了未联系到但没传截图 ⇒ 本节点未完成
      if (!hasProof) {
        return gate('requireProofFirst');
      }
      // 第2节点：还没选二次联系结果
      if (!secondContact) {
        return gate('requireSecondContactFirst');
      }
      // 第2节点：选了未联系到但没传二次截图
      if (secondContact === 'not_contacted' && !hasSecondProof) {
        return gate('requireProofFirst');
      }
    }
    // 任一次联系到客户 ⇒ 回到「先改地址、重新诊断」——有人可问，地址就还有救。
    // 本分支只可能由 stdFail 进入：abnormalNoOnsite 已排除了"已联系到"的组合（见其定义）。
    if (contact === 'contacted' || secondContact === 'contacted') {
      guidanceKey = 'modifyFirst';
      ops.push({ key: ReviewOperation.ModifyAddress, recommended: true });
    } else {
      // 两次电联均未联系到 ⇒ 终态「确认无法建联」，免司机上门直达 link5。
      // 两组截图都必填（link5 的凭证要求）。走到这里时上面的顺序门禁已保证两组都齐，
      // 这里的判据是兜底：万一将来门禁被改动，按钮也不会在缺凭证时可点。
      const proofMissing = !hasProof || !hasSecondProof;
      if (isHistory(item) && item.relatedWarehouseId != null) {
        // 历史存量已有站点 ⇒ 更新原站为 link5，不新建（否则同址双活跃站 + 命中不确定，见 plan §2.3）
        guidanceKey = 'stdFailUnreachableHistory';
        ops.push({
          key: ReviewOperation.UpdateSite,
          recommended: true,
          labelKey: 'updateSiteUnreachable',
          subtitleKey: 'unreachableNoOnsite',
          disabled: proofMissing,
        });
      } else {
        // 系统进单 / BD：无既有站点 ⇒ 用条目原始地址建站并直接置 link5
        guidanceKey = 'stdFailUnreachableNew';
        ops.push({
          key: ReviewOperation.CreateSite,
          recommended: true,
          labelKey: 'createSiteUnreachable',
          subtitleKey: 'unreachableNoOnsite',
          disabled: proofMissing,
        });
      }
    }
  } else if (contact === 'not_contacted') {
    if (isHistory(item)) {
      // 未联系 + 历史存量：站点已存在 → 仅确认/创建当日预约单（对齐原型 opHistoryNot，不给合并）
      // 引导文案数据驱动：当日已有单→核对并确认；当日暂无单→为该地址创建预约单
      guidanceKey = item.hasTodayAppointment ? 'notContactedHistoryConfirm' : 'notContactedHistoryCreate';
      // 与「未联系 + 系统进单/BD」同口径：未联系必须先留联络证据，未上传截图则置灰。
      // 此前历史存量分支漏判 hasProof，导致不传截图也能直接确认/创建当日预约单。
      ops.push(
        item.hasTodayAppointment
          ? { key: ReviewOperation.ConfirmAppointment, recommended: true, disabled: !hasProof }
          : { key: ReviewOperation.CreateAppointment, recommended: true, disabled: !hasProof },
      );
    } else {
      // 未联系 + 系统进单 / BD：仅创建新站点（未建联）（对齐原型 opNot，不给合并）
      // 需先上传联络记录截图作为凭证，未上传则置灰不可操作（PRD：未联系需留联络证据）
      guidanceKey = 'notContactedSystem';
      ops.push({
        key: ReviewOperation.CreateSite,
        recommended: true,
        labelKey: 'createSiteUnlinked',
        subtitleKey: 'createUnlinked',
        disabled: !hasProof,
      });
    }
  } else if (similar) {
    // 已联系 + 有相似（相似诊断后端已排除来源站点自身，此处为与其他真站点相似）
    if (isHistory(item) && item.relatedWarehouseId != null) {
      // 历史存量（G7，对齐原型 opSimilar+历史）：合并到已有(推荐) + 更新原站(确认非同一站点时)
      guidanceKey = 'contactedHistorySimilar';
      ops.push({ key: ReviewOperation.MergeToExisting, recommended: true });
      ops.push({ key: ReviewOperation.UpdateSite, recommended: false, prefixKey: 'ifNotSame' });
      // 2.1 需求点2：历史存量也要能做 AB 映射（客户确认实际在 B 处上门）。
      // 与「更新站点」同属「确认非同一站点时」的次选，主按钮与推荐项完全不变。
      // 后端语义：一次 AB 映射 = 整站作废（关联地址一并映射到同一个 B + 删站），见 plan §3.4/§3.5。
      ops.push({ key: ReviewOperation.AbMapping, recommended: false });
    } else {
      // 系统进单/BD（对齐原型 opSimilar）：合并到已有(推荐) + 确认非同一站点时[创建新站点 + AB地址映射]
      guidanceKey = 'contactedSimilar';
      ops.push({ key: ReviewOperation.MergeToExisting, recommended: true });
      ops.push({ key: ReviewOperation.CreateSite, recommended: false, prefixKey: 'ifNotSame' });
      // AB 地址映射：与创建新站点同属「确认非同一站点时」的次选（原型 2.4.3）
      ops.push({ key: ReviewOperation.AbMapping, recommended: false });
    }
  } else if (isHistory(item) && item.relatedWarehouseId != null) {
    // 已联系 + 历史存量 + 无相似（G7）：该地址已绑定既有站点(related)，更新原站建联，不重复建站。
    // related 为空(异常数据)则退回按诊断走(创建新站点)。
    guidanceKey = 'contactedHistory';
    ops.push({ key: ReviewOperation.UpdateSite, recommended: true });
    // 2.1 需求点2：诊断=新地址的历史存量条目同样提供 AB 映射（原型两张图分别是新地址与相似站点，两种都要有）
    ops.push({ key: ReviewOperation.AbMapping, recommended: false, prefixKey: 'otherWay', subtitleKey: 'abScenario' });
  } else {
    // 已联系 + 系统/BD + 新地址（对齐原型 opNewFlow）：创建新站点(推荐) + AB映射
    guidanceKey = 'contactedSystem';
    // 主按钮「创建新站点」不带副标题（原型无；群名称/群ID 由建站抽屉 requireGroup 强制必填，无需引导文字）
    ops.push({ key: ReviewOperation.CreateSite, recommended: true });
    // AB 仓映射作为「其他处理方式」呈现，附场景说明（原型：新地址为 A 仓、客户要求上门 B 仓时用）
    ops.push({ key: ReviewOperation.AbMapping, recommended: false, prefixKey: 'otherWay', subtitleKey: 'abScenario' });
  }
  return {
    diagnosisHintKey: hintKey,
    requireContactFirst: false,
    requireSecondContactFirst: false,
    requireProofFirst: false,
    guidanceKey,
    operations: ops,
  };
}

/**
 * 该条目当前是否允许「上报地址异常」（2.2 需求点1）。
 *
 * <p><b>判据必须与后端 PickupAddressAbnormalReportService.checkReviewPoolReportable 保持一致</b>，
 * 改一边要同步另一边 —— 前端放行而后端拒绝，运营点了只收到一个错误码；
 * 前端拦住而后端允许，功能等于没上线。
 *
 * <ol>
 *   <li><b>诊断不是「标准化失败」</b>：那类条目 2.1 已有独立的免上门流程，重复上报只会让统计口径混乱；</li>
 *   <li><b>首次建联结果为「未联系到」</b>：PRD 限定"首次建联结果为无法建联"才可上报。</li>
 * </ol>
 *
 * <p>后端还有第三道「条目未处理」的判据，这里**刻意不重复**：审核池列表本身只返回未处理
 * （process_status 0/2）的条目，前端拿不到已处理条目，加一个恒真的判断只会让人误以为
 * ReviewPoolItem 上有 processStatus 字段（它没有）。已处理条目的上报入口在站点侧。
 */
export function canReportAddressAbnormal(
  item: ReviewPoolItem,
  contact?: ReviewContactResult,
): boolean {
  if (item.diagnosisType === ReviewDiagnosisType.StandardizeFailed) return false;
  return contact === 'not_contacted';
}
